import React, { useEffect } from 'react';
import Navbar from './Navbar';
import '../styles.css';

function Home() {
  // Load YouTube Iframe API
  useEffect(() => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }, []);

  return (
    <div className="font-sans text-slate-800 bg-slate-50 min-h-screen flex flex-col">
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200/60 shadow-sm/50">
        <Navbar showSignUpButton={true} />
      </div>

      <main className="flex-1 w-full flex flex-col items-center">
        {/* Hero Section */}
        <section className="w-full bg-white flex flex-col items-center pt-24 pb-32 px-6 lg:px-8">
          <div className="max-w-6xl w-full flex flex-col lg:flex-row items-center gap-16">
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-4xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
                You might be overpaying property taxes by <span className="text-sky-500">$800–$2,500/year</span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0">
                Instantly check your home’s value and get the exact evidence needed to lower your assessment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="/signup" className="inline-flex justify-center items-center px-8 py-4 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold text-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                  Check My Property (Free)
                </a>
              </div>
            </div>
            {/* Abstract Dashboard Graphic */}
            <div className="flex-1 w-full max-w-lg lg:max-w-none">
              <div className="relative bg-white border border-slate-200 rounded-2xl shadow-2xl p-6 ring-1 ring-slate-900/5">
                <div className="flex justify-between items-center mb-6">
                  <div className="h-4 w-32 bg-slate-200 rounded-full"></div>
                  <div className="h-6 w-24 bg-teal-100 text-teal-700 text-xs font-bold rounded-full flex items-center justify-center">Estimated Value</div>
                </div>
                <div className="space-y-4">
                  <div className="h-10 w-48 bg-slate-100 rounded-lg"></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="h-24 bg-slate-50 border border-slate-100 rounded-xl"></div>
                    <div className="h-24 bg-slate-50 border border-slate-100 rounded-xl"></div>
                  </div>
                  <div className="h-32 bg-slate-50 border border-slate-100 rounded-xl flex items-end px-4 gap-2 pb-2">
                     <div className="w-1/6 bg-sky-400 rounded-t h-1/2"></div>
                     <div className="w-1/6 bg-sky-300 rounded-t h-3/4"></div>
                     <div className="w-1/6 bg-sky-500 rounded-t h-full"></div>
                     <div className="w-1/6 bg-sky-400 rounded-t h-2/3"></div>
                     <div className="w-1/6 bg-sky-300 rounded-t h-1/3"></div>
                  </div>
                </div>
                {/* Decorative floating element */}
                <div className="absolute -right-6 -bottom-6 bg-white border border-slate-200 rounded-xl shadow-xl p-4 flex items-center gap-4 hidden md:flex">
                   <div className="h-10 w-10 bg-teal-500 rounded-full flex items-center justify-center shadow-inner">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                   </div>
                   <div>
                     <div className="text-sm font-bold text-slate-800">Appeal Ready</div>
                     <div className="text-xs text-slate-500">Comps analyzed</div>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Appeal Section - Updated to more professional tone */}
        <section className="w-full bg-slate-50 py-24 flex flex-col items-center">
          <div className="max-w-4xl px-6 w-full">
             <div className="text-center mb-16">
               <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">Most homeowners never question their assessment—and overpay because of it</h2>
               <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
                 Property tax assessments are often based on outdated or inaccurate data. If your home is overvalued, you could be paying more than you should—year after year.
               </p>
             </div>

             <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
               <div className="space-y-6">
                 <h3 className="text-2xl font-bold text-slate-900">Casalytix helps you:</h3>
                 <ul className="space-y-4">
                   <li className="flex items-start gap-3">
                     <div className="mt-1 flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center">
                       <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                     </div>
                     <span className="text-slate-700 font-medium">Identify if your home is over-assessed</span>
                   </li>
                   <li className="flex items-start gap-3">
                     <div className="mt-1 flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center">
                       <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                     </div>
                     <span className="text-slate-700 font-medium">Back it up with real comparable sales</span>
                   </li>
                   <li className="flex items-start gap-3">
                     <div className="mt-1 flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center">
                       <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                     </div>
                     <span className="text-slate-700 font-medium">Generate a report you can actually use to appeal</span>
                   </li>
                 </ul>
                 <p className="text-slate-600 pt-4">Many homeowners save hundreds to thousands per year—and those savings can carry forward.</p>
               </div>
               <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-100">
                  <div className="text-center">
                    <div className="text-sky-500 font-bold mb-2 uppercase tracking-wider text-sm">Potential Savings</div>
                    <div className="text-5xl font-black text-slate-900 mb-6">$1,250<span className="text-lg text-slate-400 font-normal ml-1">/avg</span></div>
                    <a href="/signup" className="w-full inline-flex justify-center items-center px-8 py-4 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-lg transition-all shadow-md">
                      See If I’m Overpaying
                    </a>
                  </div>
               </div>
             </div>
          </div>
        </section>

        {/* How It Works (3 Steps) */}
        <section className="w-full bg-white py-24 flex flex-col items-center border-t border-slate-100">
          <div className="max-w-5xl px-6 w-full">
            <h2 className="text-3xl lg:text-4xl font-bold text-center text-slate-900 mb-16">Lower Your Property Taxes in <span className="text-sky-500">3 Steps</span></h2>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              
              {/* Step 1 */}
              <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <span className="text-8xl font-black text-sky-600">1</span>
                </div>
                <div className="h-12 w-12 bg-sky-100 text-sky-500 rounded-2xl flex items-center justify-center font-bold text-2xl mb-6 shadow-sm">1</div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">1. See If You’re Overpaying (Free)</h3>
                <p className="text-slate-600 leading-relaxed">Instantly check your home’s estimated value and compare it to your current assessment.</p>
              </div>

              {/* Step 2 */}
              <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <span className="text-8xl font-black text-sky-600">2</span>
                </div>
                <div className="h-12 w-12 bg-sky-100 text-sky-500 rounded-2xl flex items-center justify-center font-bold text-2xl mb-6 shadow-sm">2</div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">2. Unlock Your Evidence</h3>
                <p className="text-slate-600 leading-relaxed">Get verified comparable sales and a clear valuation range to support your appeal.</p>
              </div>

              {/* Step 3 */}
              <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <span className="text-8xl font-black text-sky-600">3</span>
                </div>
                <div className="h-12 w-12 bg-sky-100 text-sky-500 rounded-2xl flex items-center justify-center font-bold text-2xl mb-6 shadow-sm">3</div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">3. Submit Your Appeal in Minutes</h3>
                <p className="text-slate-600 leading-relaxed">Download your appeal-ready report and follow simple steps to file before your deadline.</p>
              </div>

            </div>
            <p className="text-center text-sm text-slate-400 italic mb-10">Note: Processes vary by location. Check your local tax authority’s website for exact details.</p>
            <div className="text-center">
               <a href="/signup" className="inline-flex justify-center items-center px-8 py-4 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold text-lg shadow-md hover:-translate-y-0.5 transition-all">Get Started</a>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full bg-slate-50 py-24 flex flex-col items-center">
          <div className="max-w-6xl px-6 w-full text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-16">Everything You Need to Save</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 mx-auto bg-teal-50 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Discover Comparable Sales</h3>
                <p className="text-slate-600">Find homes like yours that sold for less to easily prove your property’s true market value.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 mx-auto bg-indigo-50 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Get an Accurate Valuation</h3>
                <p className="text-slate-600">Receive a reliable, data-driven estimate of your home’s worth to argue for a lower tax assessment.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 mx-auto bg-rose-50 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Generate an Appeal Report</h3>
                <p className="text-slate-600">Download a ready-to-use PDF report highlighting your evidence securely.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Demo / Video Section */}
        <section className="w-full bg-white py-24 flex flex-col items-center">
          <div className="max-w-4xl px-6 w-full text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-12">Watch How Easy It Is to Save</h2>
            <div className="relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl border-4 border-slate-100 bg-black group">
              <div className="absolute inset-0 bg-slate-900/10 pointer-events-none group-hover:bg-transparent transition-colors z-10 duration-500"></div>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/RS8_cAHFCbI?enablejsapi=1&modestbranding=1&rel=0&showinfo=0"
                title="Casalytix: How It Works"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="w-full bg-white py-8 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 text-center text-slate-500">
          <p>© {new Date().getFullYear()} Casalytix. All rights reserved. | <a href="/privacy-policy" className="hover:text-sky-500 transition-colors">Privacy Policy</a></p>
        </div>
      </footer>
    </div>
  );
}

export default Home;